import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { documentService } from '../../services/api';
import { FileText, Download, Archive, RotateCcw, Brain, Loader, EyeOff, Search, RefreshCw, Edit3, Share2, Layers, FolderInput, Users, Trash2, X } from 'lucide-react';
import DocumentPreview from './DocumentPreview';
import ArchiveDocumentModal from './ArchiveDocumentModal';
import RestoreDocumentModal from './RestoreDocumentModal';
import DocumentAIInsights from './DocumentAIInsights';
import VersionHistory from './VersionHistory';
import CollaboraEditor from './CollaboraEditor';
import ShareDocumentModal from './ShareDocumentModal';
import MoveDocumentModal from './MoveDocumentModal';

const EDITABLE_MIME_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.oasis.opendocument.presentation',
]);

const DocumentList = ({
  caseId = null,
  projectId = null,
  communicationId = null,
  folderId = null,
  limit = 10,
  showActions = true,
  className = "",
  onArchive = null,  // Callback to trigger folder tree refresh after archiving
  sortBy = null,
  sortOrder = null
}) => {
  const [documents, setDocuments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewDocument, setPreviewDocument] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [archiveDocument, setArchiveDocument] = useState(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [restoreDocument, setRestoreDocument] = useState(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [aiInsightsDocument, setAiInsightsDocument] = useState(null);
  const [showAiInsights, setShowAiInsights] = useState(false);
  const navigate = useNavigate();
  const [processingDocs, setProcessingDocs] = useState(new Set());
  const [aiError, setAiError] = useState('');
  const [versionHistoryDoc, setVersionHistoryDoc] = useState(null);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [replaceDoc, setReplaceDoc] = useState(null);
  const replaceFileRef = React.useRef(null);
  const [editingDocId, setEditingDocId] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [shareDocument, setShareDocument] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [moveDocument, setMoveDocument] = useState(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [sharingPopoverDocId, setSharingPopoverDocId] = useState(null);
  const [sharingDetails, setSharingDetails] = useState({});
  const [sharingLoading, setSharingLoading] = useState(false);

  useEffect(() => {
    if (!sharingPopoverDocId) return;
    const handleClickOutside = () => setSharingPopoverDocId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [sharingPopoverDocId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const pageSize = showAll ? 50 : limit;
      const params = {
        page_size: pageSize,
        ...(showAll && { page }),
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(caseId && { case_id: caseId }),
        ...(projectId && { project_id: projectId }),
        ...(communicationId && { communication_id: communicationId }),
        ...(folderId && { folder_id: folderId }),  // Backend handles 'unfiled' as a special string
        ...(sortBy && { sort_by: sortBy }),
        ...(sortOrder && { sort_order: sortOrder })
      };

      const response = await documentService.getDocuments(params);
      setDocuments(response.data.documents);
      setTotalCount(response.data.total_count);
      setTotalPages(response.data.total_pages);
    } catch (err) {
      setError('Failed to load documents');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [caseId, projectId, communicationId, folderId, limit, showAll, page, debouncedSearch, sortBy, sortOrder]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleDownload = async (doc) => {
    try {
      const response = await documentService.getDownloadUrl(doc.id, true); // force_download=true
      window.open(response.data.download_url, '_blank');
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handleArchive = (doc) => {
    setArchiveDocument(doc);
    setShowArchiveModal(true);
  };

  const handleArchiveConfirm = async (reason) => {
    if (!archiveDocument) return;

    try {
      await documentService.archiveDocument(archiveDocument.id, reason);
      await loadDocuments();
      console.log(`Document "${archiveDocument.original_file_name}" archived successfully`);

      // Trigger folder tree refresh if callback provided
      if (onArchive) {
        onArchive();
      }
    } catch (err) {
      console.error('Archive failed:', err);
      throw err; // Let the modal handle the error display
    }
  };

  const closeArchiveModal = () => {
    setShowArchiveModal(false);
    setArchiveDocument(null);
  };

  const handleRestore = (doc) => {
    setRestoreDocument(doc);
    setShowRestoreModal(true);
  };

  const handleRestoreConfirm = async () => {
    if (!restoreDocument) return;

    try {
      await documentService.restoreDocument(restoreDocument.id);
      await loadDocuments();
      console.log(`Document "${restoreDocument.original_file_name}" restored successfully`);

      // Trigger folder tree refresh if callback provided
      if (onArchive) {
        onArchive();
      }
    } catch (err) {
      console.error('Restore failed:', err);
      throw err; // Let the modal handle the error display
    }
  };

  const closeRestoreModal = () => {
    setShowRestoreModal(false);
    setRestoreDocument(null);
  };

  const handlePreview = (doc) => {
    setPreviewDocument(doc);
    setShowPreview(true);
  };

  const closePreview = () => {
    setShowPreview(false);
    setPreviewDocument(null);
  };

  const handleAiInsights = (doc) => {
    setAiInsightsDocument(doc);
    setShowAiInsights(true);
  };

  const closeAiInsights = () => {
    setShowAiInsights(false);
    setAiInsightsDocument(null);
  };

  const handleAiReprocess = () => {
    // Reload documents to get updated AI data
    loadDocuments();
  };

  const handleVersionHistory = (doc) => {
    setVersionHistoryDoc(doc);
    setShowVersionHistory(true);
  };

  const closeVersionHistory = () => {
    setShowVersionHistory(false);
    setVersionHistoryDoc(null);
  };

  const handleVersionRestore = () => {
    loadDocuments();
  };

  const handleReplaceClick = (doc) => {
    setReplaceDoc(doc);
    // Trigger hidden file input
    if (replaceFileRef.current) {
      replaceFileRef.current.click();
    }
  };

  const handleReplaceFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !replaceDoc) return;

    try {
      const formData = new FormData();
      formData.append('file', file);
      await documentService.replaceFile(replaceDoc.id, formData);
      await loadDocuments();
    } catch (err) {
      console.error('Replace failed:', err);
    } finally {
      setReplaceDoc(null);
      if (replaceFileRef.current) {
        replaceFileRef.current.value = '';
      }
    }
  };

  const handleEdit = (doc) => {
    setEditingDocId(doc.id);
    setShowEditor(true);
  };

  const handleEditorClose = () => {
    setShowEditor(false);
    setEditingDocId(null);
  };

  const handleEditorSave = () => {
    loadDocuments();
  };

  const handleShare = (doc) => {
    setShareDocument(doc);
    setShowShareModal(true);
  };

  const closeShareModal = () => {
    setShowShareModal(false);
    setShareDocument(null);
    loadDocuments();
  };

  const handleSharingBadgeClick = async (e, docId) => {
    e.stopPropagation();
    if (sharingPopoverDocId === docId) {
      setSharingPopoverDocId(null);
      return;
    }
    setSharingPopoverDocId(docId);
    if (!sharingDetails[docId]) {
      setSharingLoading(true);
      try {
        const response = await documentService.getSharingStatus(docId);
        setSharingDetails(prev => ({ ...prev, [docId]: response.data.data }));
      } catch (err) {
        console.error('Failed to load sharing details:', err);
      } finally {
        setSharingLoading(false);
      }
    }
  };

  const handleUnshare = async (e, docId, clientId) => {
    e.stopPropagation();
    try {
      await documentService.unshareWithClient(docId, clientId);
      setSharingDetails(prev => ({
        ...prev,
        [docId]: (prev[docId] || []).filter(s => s.client_id !== clientId)
      }));
      loadDocuments();
    } catch (err) {
      console.error('Unshare failed:', err);
    }
  };

  const handleSummarize = async (doc) => {
    try {
      setProcessingDocs(prev => new Set([...prev, doc.id]));
      await documentService.processAi(doc.id);

      // Poll for completion
      const pollForCompletion = async () => {
        try {
          const response = await documentService.getAiStatus(doc.id);
          if (response.data.ai_processed) {
            setProcessingDocs(prev => {
              const newSet = new Set(prev);
              newSet.delete(doc.id);
              return newSet;
            });
            loadDocuments(); // Refresh to show new AI data
          } else {
            setTimeout(pollForCompletion, 2000); // Check again in 2 seconds
          }
        } catch (error) {
          console.error('Error checking AI status:', error);
          setProcessingDocs(prev => {
            const newSet = new Set(prev);
            newSet.delete(doc.id);
            return newSet;
          });
        }
      };

      setTimeout(pollForCompletion, 3000); // Start polling after 3 seconds
    } catch (error) {
      console.error('Error starting AI processing:', error);
      const detail = error.response?.data?.detail;
      setAiError(detail || 'Failed to start AI processing. Please try again.');
      setProcessingDocs(prev => {
        const newSet = new Set(prev);
        newSet.delete(doc.id);
        return newSet;
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const canProcessWithAI = (mimeType) => {
    // Check if document type is supported for AI text extraction
    const supportedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
    ];

    return supportedTypes.includes(mimeType) || (mimeType && mimeType.startsWith('text/'));
  };

  if (loading) {
    return (
      <div className={`text-center py-4 ${className}`}>
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500 mx-auto"></div>
        <p className="mt-2 text-sm text-gray-600">Loading documents...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-center py-4 ${className}`}>
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  if (documents.length === 0 && !showAll) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <FileText className="mx-auto h-8 w-8 text-gray-400" />
        <p className="mt-2 text-sm text-gray-500">No documents found</p>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {showAll && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Search documents..."
            className="pl-9 w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      )}
      {documents.length === 0 && showAll && (
        <div className="text-center py-6">
          <FileText className="mx-auto h-8 w-8 text-gray-400" />
          <p className="mt-2 text-sm text-gray-500">No documents match your search</p>
        </div>
      )}
      {aiError && (
        <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
          <span className="text-sm text-red-700">{aiError}</span>
          <button
            onClick={() => setAiError('')}
            className="ml-3 text-red-500 hover:text-red-700 text-sm font-medium flex-shrink-0"
          >
            Dismiss
          </button>
        </div>
      )}
      {documents.map((doc) => (
        <div
          key={doc.id}
          draggable={true}
          onDragStart={(e) => {
            e.dataTransfer.setData('application/document-id', doc.id);
            e.dataTransfer.effectAllowed = 'move';
          }}
          role="button"
          tabIndex={0}
          onClick={() => handlePreview(doc)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handlePreview(doc); } }}
          className="flex items-start justify-between gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-move transition-colors"
        >
          <div className="flex items-start space-x-3 flex-1 min-w-0">
            <FileText className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />

            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 break-words" title={doc.original_file_name}>
                {doc.original_file_name}
              </p>
              <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-1">
                <span className="text-xs text-gray-500 flex-shrink-0">
                  {formatFileSize(doc.file_size)}
                </span>
                <span className="text-xs text-gray-500 flex-shrink-0">
                  {formatDate(doc.created_at)}
                </span>
                {doc.uploaded_by_name && (
                  <span className="text-xs text-gray-500 flex-shrink-0">
                    by {doc.uploaded_by_name}
                  </span>
                )}
                {doc.document_type && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 flex-shrink-0">
                    {doc.document_type.replace('_', ' ')}
                  </span>
                )}
                {doc.ai_processed_at && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 flex-shrink-0">
                    <Brain className="h-3 w-3 mr-1" />
                    AI Analyzed
                  </span>
                )}
                {doc.ai_processing_error && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 flex-shrink-0">
                    <Brain className="h-3 w-3 mr-1" />
                    AI Error
                  </span>
                )}
                {processingDocs.has(doc.id) && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 flex-shrink-0">
                    <Loader className="h-3 w-3 mr-1 animate-spin" />
                    Processing...
                  </span>
                )}
                {doc.archived_at && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 flex-shrink-0">
                    <Archive className="h-3 w-3 mr-1" />
                    Archived
                  </span>
                )}
                {doc.version_count > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVersionHistory(doc);
                    }}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 flex-shrink-0 cursor-pointer transition-colors"
                    title="View version history"
                  >
                    <Layers className="h-3 w-3 mr-1" />
                    {doc.version_count} versions
                  </button>
                )}
                {doc.shared_client_count > 0 && (
                  <div className="relative flex-shrink-0">
                    <button
                      onClick={(e) => handleSharingBadgeClick(e, doc.id)}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer transition-colors"
                      title="Shared with client — click to view"
                    >
                      <Users className="h-3 w-3 mr-1" />
                      Shared ({doc.shared_client_count})
                    </button>
                    {sharingPopoverDocId === doc.id && (
                      <div className="absolute z-50 mt-1 left-0 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-gray-700">Shared with</span>
                          <button onClick={(e) => { e.stopPropagation(); setSharingPopoverDocId(null); }} className="text-gray-400 hover:text-gray-600">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                        {sharingLoading && !sharingDetails[doc.id] ? (
                          <div className="flex items-center justify-center py-2">
                            <Loader className="h-4 w-4 animate-spin text-gray-400" />
                          </div>
                        ) : (sharingDetails[doc.id] || []).length === 0 ? (
                          <p className="text-xs text-gray-500 py-1">No sharing details available</p>
                        ) : (
                          <ul className="space-y-2">
                            {(sharingDetails[doc.id] || []).map((share) => (
                              <li key={share.client_id} className="flex items-center justify-between text-xs">
                                <div className="min-w-0 flex-1">
                                  <p className="font-medium text-gray-900 truncate">{share.client_name}</p>
                                  <p className="text-gray-500 truncate">{share.shared_at ? new Date(share.shared_at).toLocaleDateString() : ''}</p>
                                </div>
                                <button
                                  onClick={(e) => handleUnshare(e, doc.id, share.client_id)}
                                  className="ml-2 text-red-400 hover:text-red-600 p-0.5 flex-shrink-0"
                                  title={`Unshare with ${share.client_name}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {doc.ai_summary && (
                <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-700 break-words">
                  <strong>AI Summary:</strong> {doc.ai_summary}
                </div>
              )}
            </div>
          </div>

          {showActions && (
            <div className="flex items-center space-x-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
              {/* AI Processing Button - only show for supported file types */}
              {canProcessWithAI(doc.mime_type) && !doc.ai_processed_at && !processingDocs.has(doc.id) && (
                <button
                  onClick={() => handleSummarize(doc)}
                  className="text-purple-600 hover:text-purple-900 p-1 hover:bg-purple-50 rounded transition-colors"
                  title="Summarize with AI"
                >
                  <Brain className="h-4 w-4" />
                </button>
              )}

              {/* AI Insights Button (only show if processed) */}
              {doc.ai_processed_at && (
                <button
                  onClick={() => handleAiInsights(doc)}
                  className="text-purple-600 hover:text-purple-900 p-1 hover:bg-purple-50 rounded transition-colors"
                  title="View AI Insights"
                >
                  <Brain className="h-4 w-4" />
                </button>
              )}

              {EDITABLE_MIME_TYPES.has(doc.mime_type) && (
                <button
                  onClick={() => handleEdit(doc)}
                  className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded transition-colors"
                  title="Edit in Browser"
                >
                  <Edit3 className="h-4 w-4" />
                </button>
              )}

              <button
                onClick={() => handleDownload(doc)}
                className="text-yellow-600 hover:text-gray-900 p-1 hover:bg-gray-50 rounded transition-colors"
                title="Download"
              >
                <Download className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleShare(doc)}
                className="text-blue-500 hover:text-blue-700 p-1 hover:bg-blue-50 rounded transition-colors"
                title="Share with Client"
              >
                <Share2 className="h-4 w-4" />
              </button>
<button
                onClick={() => handleReplaceClick(doc)}
                className="text-gray-500 hover:text-gray-900 p-1 hover:bg-gray-50 rounded transition-colors"
                title="Replace File"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
              <button
                onClick={() => navigate(`/documents/${doc.id}/redact`)}
                className="text-gray-500 hover:text-gray-900 p-1 hover:bg-gray-50 rounded transition-colors"
                title="Redact Document"
              >
                <EyeOff className="h-4 w-4" />
              </button>
              {!doc.archived_at && (
                <button
                  onClick={() => { setMoveDocument(doc); setShowMoveModal(true); }}
                  className="text-blue-500 hover:text-blue-700 p-1 hover:bg-blue-50 rounded transition-colors"
                  title="Move to Another Case"
                >
                  <FolderInput className="h-4 w-4" />
                </button>
              )}
              {doc.archived_at ? (
                <button
                  onClick={() => handleRestore(doc)}
                  className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded transition-colors"
                  title="Restore from Archive"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={() => handleArchive(doc)}
                  className="text-yellow-600 hover:text-yellow-800 p-1 hover:bg-yellow-50 rounded transition-colors"
                  title="Archive Document"
                >
                  <Archive className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>
      ))}
      
      {!showAll && totalCount > limit && (
        <div className="text-center pt-2">
          <button
            onClick={() => { setShowAll(true); setPage(1); }}
            className="text-sm text-yellow-600 hover:text-yellow-800 font-medium"
          >
            View all {totalCount} documents
          </button>
        </div>
      )}

      {showAll && totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-xs text-gray-500">
            Page {page} of {totalPages} ({totalCount} documents)
          </span>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setPage(p => p - 1)}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page === totalPages}
              className="px-3 py-1 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
      
      {/* Document Preview Modal */}
      <DocumentPreview
        document={previewDocument}
        isOpen={showPreview}
        onClose={closePreview}
      />
      
      {/* Archive Document Modal */}
      <ArchiveDocumentModal
        document={archiveDocument}
        isOpen={showArchiveModal}
        onClose={closeArchiveModal}
        onConfirm={handleArchiveConfirm}
      />
      
      {/* Restore Document Modal */}
      <RestoreDocumentModal
        document={restoreDocument}
        isOpen={showRestoreModal}
        onClose={closeRestoreModal}
        onConfirm={handleRestoreConfirm}
      />

      {/* Move Document Modal */}
      <MoveDocumentModal
        document={moveDocument}
        isOpen={showMoveModal}
        onClose={() => { setShowMoveModal(false); setMoveDocument(null); }}
        onMoved={() => loadDocuments()}
      />

      {/* AI Insights Modal */}
      {showAiInsights && (
        <DocumentAIInsights
          document={aiInsightsDocument}
          onClose={closeAiInsights}
          onReprocess={handleAiReprocess}
        />
      )}

      {/* Version History Panel */}
      <VersionHistory
        document={versionHistoryDoc}
        isOpen={showVersionHistory}
        onClose={closeVersionHistory}
        onRestore={handleVersionRestore}
      />

      {/* Collabora Editor Modal */}
      <CollaboraEditor
        documentId={editingDocId}
        isOpen={showEditor}
        onClose={handleEditorClose}
        onSave={handleEditorSave}
      />

      {/* Share Document Modal */}
      <ShareDocumentModal
        document={shareDocument}
        caseId={caseId}
        isOpen={showShareModal}
        onClose={closeShareModal}
      />

      {/* Hidden file input for Replace */}
      <input
        ref={replaceFileRef}
        type="file"
        className="hidden"
        onChange={handleReplaceFile}
      />
    </div>
  );
};

export default DocumentList;
